/**
 * PortfolioV2 — the Portfolio tab (Trading IA redesign). LIVE at /trading/portfolio
 * since the 2026-07-19 swap; the old PortfolioPage.js is retired (unwired, kept on
 * disk for reference). Scope: context bar + scalable account picker + Strategy⟷IBKR
 * toggle + Strategy-panel KPI strip + compressed TWR hero (real `portfolio-twr`
 * endpoint, client-side range rebase) + allocation & holdings block + ledger/tracker/
 * broker reconciliation + IBKR broker panel (with unattributed holdings) +
 * per-security netted executions. See Plans/trading_ia_redesign.md.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useAuth } from '../../../context/AuthContext';
import AccountCombobox from './AccountCombobox';
import useSharedAccount from './useSharedAccount';
import MiniTwrChart from './MiniTwrChart';
import AllocationBlock from './AllocationBlock';
import ReconcileTable from './ReconcileTable';
import ExecutionsBlock from './ExecutionsBlock';
import IbkrPanel from './IbkrPanel';
import { rebaseSeries, RANGES, RANGE_LABELS } from './twrRange';

// Account selection is shared across the whole Portfolio pillar (Overview /
// Performance / Orders) via useSharedAccount — see that hook for the storage key.

const fmt$ = (n, d = 0) =>
  n == null || isNaN(n) ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const fmtPct = (n, d = 1) => (n == null || isNaN(n) ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(d)}%`);
const pnlClr = (n) => (n == null || isNaN(n) ? 'text-gray-800 dark:text-gray-100' : n > 0 ? 'text-green-400' : n < 0 ? 'text-red-400' : 'text-gray-400');
const signed$ = (n) => (n == null || isNaN(n) ? '—' : `${n >= 0 ? '+' : ''}${fmt$(n)}`);

const Kpi = ({ label, value, valueClass = 'text-gray-800 dark:text-gray-100', sub, subClass = 'text-gray-500' }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
    <div className="text-[9px] uppercase tracking-widest text-gray-400">{label}</div>
    <div className={`mt-1.5 font-mono text-[17px] tabular-nums ${valueClass}`}>{value}</div>
    {sub && <div className={`mt-0.5 text-[10px] ${subClass}`}>{sub}</div>}
  </div>
);

const PortfolioV2 = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;

  // ── account state (shared across the whole Portfolio pillar) ──
  const [accounts, setAccounts] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useSharedAccount();
  useEffect(() => {
    tradingService.listIBKRAccounts()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.results || []);
        setAccounts(list);
        if (selectedAccountId == null) {
          const def = list.find((a) => a.is_default);
          if (def) setSelectedAccountId(def.id);
        } else if (!list.find((a) => a.id === selectedAccountId)) {
          setSelectedAccountId(null);
        }
      })
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── panel toggle ──
  const [panelView, setPanelView] = useState('strategy'); // 'strategy' | 'ibkr'

  // ── TWR curve (strategy panel) ──
  const [twr, setTwr] = useState(null);
  const [twrLoading, setTwrLoading] = useState(false);
  const [range, setRange] = useState('YTD');

  // ── strategy EOD equity curve → KPI tiles (same derivation as PortfolioPage) ──
  const [stratCurve, setStratCurve] = useState([]);
  // ── allocation tree — security + bucket + strategy-level sector rollup (ledger, no IBKR) ──
  const [allocTree, setAllocTree] = useState(null);
  const [allocLoading, setAllocLoading] = useState(false);
  // ── reconciliation (ledger vs tracker) + lazily-loaded broker column ──
  const [reconRows, setReconRows] = useState([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [ibkr, setIbkr] = useState(null);
  const [ibkrLoading, setIbkrLoading] = useState(false);
  const [ibkrError, setIbkrError] = useState(null);
  const [reloggingIn, setReloggingIn] = useState(false);
  const ibkrLoadedRef = useRef(false);
  // ── executions — per-security, per-trading-day netted summary (own data, EOD) ──
  const [exec, setExec] = useState(null);       // { rows, n_days, n_symbols, ... }
  const [execLoading, setExecLoading] = useState(false);
  const [execDays, setExecDays] = useState('30');

  const loadStrategy = useCallback(() => {
    setTwrLoading(true);
    setAllocLoading(true);
    const params = selectedAccountId ? { account_id: selectedAccountId } : {};
    Promise.all([
      tradingService.getPortfolioTwr(selectedAccountId, { light: true }).then((r) => r.data).catch(() => null),
      tradingService.getPortfolioEquityCurve(params).then((r) => (Array.isArray(r.data?.series) ? r.data.series : [])).catch(() => []),
    ]).then(([twrData, curve]) => {
      setTwr(twrData);
      setStratCurve(curve);
    }).finally(() => setTwrLoading(false));
    // allocation tree (fast, EOD) — security + bucket + strategy-level sector rollup.
    // No IBKR gateway call on the strategy page; the IBKR panel (increment 4) fetches it separately.
    tradingService.getStrategyAllocationTree(selectedAccountId)
      .then((r) => setAllocTree(r.data || null)).catch(() => setAllocTree(null))
      .finally(() => setAllocLoading(false));
    // reconciliation rows (ledger vs tracker vs reserve vs central) load LAZILY on the panel's first
    // expand (loadRecon) — the endpoint is ~7 s on the live account and the panel is collapsed by default,
    // so it must not sit on the page-load path (2026-10-05). Re-fetched here only if already opened.
    reconLoadedRef.current = false;
    setReconRows([]);
  }, [selectedAccountId]);
  useEffect(() => { loadStrategy(); }, [loadStrategy]);

  const reconLoadedRef = useRef(false);
  const loadRecon = useCallback(() => {
    if (reconLoadedRef.current) return;
    reconLoadedRef.current = true;
    setReconLoading(true);
    tradingService.getStrategyPositions(selectedAccountId)
      .then((r) => setReconRows(Array.isArray(r.data?.symbols) ? r.data.symbols : []))
      .catch(() => { setReconRows([]); reconLoadedRef.current = false; })
      .finally(() => setReconLoading(false));
  }, [selectedAccountId]);

  // Executions — refetch on account or window change. Own data (EOD ledger of
  // fills), so it's fast and never touches the IBKR gateway.
  useEffect(() => {
    setExecLoading(true);
    tradingService.getStrategyExecutions(selectedAccountId, execDays)
      .then((r) => setExec(r.data || null))
      .catch(() => setExec(null))
      .finally(() => setExecLoading(false));
  }, [selectedAccountId, execDays]);

  // Reset the lazily-loaded broker read when the account changes.
  useEffect(() => { ibkrLoadedRef.current = false; setIbkr(null); setIbkrError(null); }, [selectedAccountId]);

  // Lazy IBKR fetch — fires when the reconciliation is first expanded OR the IBKR panel
  // is opened, so the strategy page never hits the gateway on load. One fetch feeds both.
  const loadIbkr = useCallback((force) => {
    if (ibkrLoadedRef.current && !force) return;
    ibkrLoadedRef.current = true;
    setIbkrLoading(true);
    setIbkrError(null);
    tradingService.getIBKRPortfolio(selectedAccountId)
      .then((r) => setIbkr(r.data || null))
      .catch((e) => setIbkrError(e.response?.data?.error || (e.code === 'ECONNABORTED' ? 'Request timed out — IBKR gateway may be unreachable' : e.message)))
      .finally(() => setIbkrLoading(false));
  }, [selectedAccountId]);
  // ReconcileTable's first expand: fetch our rows (lazy, ~7 s) and the live broker read together.
  const onReconExpand = useCallback(() => { loadRecon(); loadIbkr(); }, [loadRecon, loadIbkr]);

  // Broker qty per symbol for the reconciliation's Broker column, derived from the IBKR read.
  const brokerBySymbol = useMemo(() => {
    if (!ibkr) return null;
    const m = {};
    (ibkr.positions || []).forEach((p) => { m[p.symbol] = p.quantity; });
    return m;
  }, [ibkr]);

  // Fetch the broker read when the IBKR panel is opened.
  useEffect(() => { if (panelView === 'ibkr') loadIbkr(); }, [panelView, loadIbkr]);

  const handleRelogin = useCallback(() => {
    setReloggingIn(true);
    tradingService.reloginIBKRGateway(selectedAccountId)
      .catch(() => {})
      .finally(() => { setTimeout(() => { loadIbkr(true); setReloggingIn(false); }, 8000); });
  }, [selectedAccountId, loadIbkr]);

  const selectedAccount = useMemo(
    () => (accounts || []).find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId]);

  const strat = useMemo(() => {
    if (!stratCurve.length) {
      // No snapshot history yet — e.g. a freshly-funded MODEL account holding only cash
      // (nothing deployed). Fall back to the account's own cash_usd so AUM/CASH show the
      // balance instead of blanks. (cash_usd is a model-account field; IBKR cash comes
      // from the broker panel, so non-model accounts keep the "—" state.)
      const cash = selectedAccount?.is_model && selectedAccount.cash_usd != null
        ? Number(selectedAccount.cash_usd) : null;
      if (cash == null) return null;
      return { aum: cash, cash, dailyPnl: null, sinceInception: 0, sinceInceptionPct: 0,
               inceptionDate: null, asOfDate: null, nStrategies: 0, cashOnly: true };
    }
    const latest = stratCurve[stratCurve.length - 1];
    const first = stratCurve[0];
    const prev = stratCurve.length >= 2 ? stratCurve[stratCurve.length - 2] : null;
    return {
      aum: latest.total_equity,
      cash: latest.cash_balance,
      // Today's P&L must be FLOW-ADJUSTED. A raw total_equity delta counts a
      // capital flow as P&L: deploying a basket reclassifies account cash into
      // strategy allocations, so total_equity jumps by the deployed size with no
      // market gain (and a withdrawal would read as a fake loss). Booked P&L
      // (unrealized+realized) is flow-invariant — a contribution enters at cost
      // basis and adds zero booked P&L — so the day's true $ P&L is its delta.
      dailyPnl: prev
        ? ((latest.unrealized_pnl || 0) + (latest.realized_pnl || 0))
          - ((prev.unrealized_pnl || 0) + (prev.realized_pnl || 0))
        : null,
      sinceInception: (latest.unrealized_pnl || 0) + (latest.realized_pnl || 0),
      sinceInceptionPct: (latest.cumulative_return || 0) * 100,
      inceptionDate: first.date,
      asOfDate: latest.date,
      nStrategies: latest.n_strategies,
    };
  }, [stratCurve, selectedAccount]);

  // ── TWR window derivation (client-side rebase — no backend per range) ──
  const asOf = twr?.series?.length ? twr.series[twr.series.length - 1].date : null;
  const twrWin = useMemo(() => rebaseSeries(twr?.series || [], range, asOf), [twr, range, asOf]);
  const spyWin = useMemo(() => rebaseSeries(twr?.spy_series || [], range, asOf), [twr, range, asOf]);
  const sharpe = twr?.summary?.sharpe;
  const vol = twr?.summary?.vol;

  // today's move (%) for portfolio + benchmark, from the last two daily TWR points
  const dayPct = (series) => {
    if (!series || series.length < 2) return null;
    const a = series[series.length - 2].cumulative;
    const b = series[series.length - 1].cumulative;
    return (1 + b) / (1 + a) - 1;
  };
  const portToday = dayPct(twr?.series);
  const spyToday = dayPct(twr?.spy_series);
  const cashPct = strat && strat.aum ? (strat.cash / strat.aum) * 100 : null;

  // Pool capital tiles: NAV = true equity (infused + P&L); gross = the deployed book (exceeds NAV
  // when levered); cash = NET FINANCING (infused − deployed, negative = leverage). From the
  // account-scoped allocation tree's pool layer (financing_cash ties to the pool TWR net_cash);
  // falls back to the gross ledger figures when the pool layer is absent (multi-account / no infusions).
  const isPool = allocTree?.nav != null;
  // While the capital view is still loading (allocation tree, ~18 s on the live account) the tiles must
  // NOT show the sleeve-sum fallback as if it were NAV (2026-10-05: 178,877 "total equity" flashed before
  // the real 113,924). Seed NAV from the STORED daily pool curve (perf store `equity` = that day's V_t,
  // written at nightly materialization) and show a spinner on the rest until the live view answers.
  const storedNav = (() => {
    const s = twr?.series;
    const last = s && s.length ? s[s.length - 1] : null;
    return last && last.equity != null ? Number(last.equity) : null;
  })();
  const pending = allocLoading && !isPool;
  const navVal = isPool ? allocTree.nav : (pending ? storedNav : (strat?.aum ?? null));
  const grossVal = allocTree?.gross != null ? allocTree.gross : (pending ? null : (strat?.aum ?? null));
  const Spin = () => <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-brand-gold animate-spin align-middle" aria-label="loading" />;
  // CASH = ACTUAL cash (NAV − positions), folds central-book + reserve SHORT PROCEEDS — matches the
  // reporting feed / Bloomberg. NOT financing_cash (infused − deployed), which omits short proceeds.
  const cashIsReal = allocTree?.real_cash != null;
  const cashVal = cashIsReal ? allocTree.real_cash
                    : (allocTree?.financing_cash != null ? allocTree.financing_cash : (pending ? null : (strat?.cash ?? null)));
  const levX = navVal && grossVal ? grossVal / navVal : null;   // gross / equity = leverage multiple
  // Dividend two-stage legs (null while the lifecycle seam is off): settled cash sits inside real cash,
  // the open receivable (< 0 = a short's payable) is a NAV term of its own.
  const divCash = allocTree?.dividend_cash;
  const divRecv = allocTree?.dividend_receivable;
  const divSub = (divCash != null || divRecv != null)
    ? ` · divs: settled ${fmt$(divCash)} · receivable ${fmt$(divRecv)}` : '';

  // Range-linked P&L for the tile. % = the window's TWR (matches the chart). $ = the
  // exact book P&L when the window spans inception, else starting-equity × window TWR
  // (a flow-adjusted approximation). Label reads "Since Inception" when it spans the start.
  const windowPnl = useMemo(() => {
    const series = twr?.series || [];
    if (!series.length || !strat) return null;
    const { windowReturn, anchorDate } = twrWin;
    const isFull = anchorDate === series[0].date;
    const pct = windowReturn == null ? null : windowReturn * 100;
    let usd;
    if (isFull) {
      // ACCOUNT P&L (nav − infused) when the pool layer exists — the same basis as the % beside it (the
      // account TWR). strat.sinceInception is the SLEEVE price P&L only (Σ realized + unrealized): it omits
      // interest income, net dividends, the reserve and the central book, so it read $34,059 against an
      // account P&L of $64,381 on 2026-10-05. Legacy fallback when the pool layer is absent.
      usd = allocTree?.pnl != null ? allocTree.pnl : strat.sinceInception;
    } else {
      const eq = stratCurve.find((p) => p.date === anchorDate);
      usd = eq && windowReturn != null ? eq.total_equity * windowReturn : null;
    }
    return { usd, pct, isFull, label: isFull ? 'Since Inception' : RANGE_LABELS[range] };
  }, [twr, twrWin, strat, stratCurve, range, allocTree]);

  return (
    <div className="space-y-4">
      {/* context bar */}
      <div className="flex flex-wrap items-center gap-3">
        <AccountCombobox accounts={accounts} value={selectedAccountId} onChange={setSelectedAccountId} />
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-[#0d1117]">
          {[{ id: 'strategy', label: 'Strategy level' }, { id: 'ibkr', label: 'IBKR (broker)' }].map((t) => (
            <button
              key={t.id}
              onClick={() => setPanelView(t.id)}
              className={`px-3.5 py-1.5 text-xs rounded-md transition-colors ${
                panelView === t.id
                  ? 'bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 shadow-sm font-semibold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >{t.label}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-mono text-gray-400">
            {panelView === 'strategy' ? (strat ? `EOD · since ${strat.inceptionDate}` : 'EOD') : 'LIVE · IBKR gateway'}
          </span>
          <button
            onClick={() => (panelView === 'ibkr' ? loadIbkr(true) : loadStrategy())}
            disabled={panelView === 'ibkr' ? ibkrLoading : twrLoading}
            title={panelView === 'ibkr' ? 'Refresh broker positions' : 'Refresh strategy data'}
            className={`px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-brand-gold hover:border-brand-gold transition-colors ${(panelView === 'ibkr' ? ibkrLoading : twrLoading) ? 'opacity-50 animate-spin' : ''}`}
          >↻</button>
        </div>
      </div>

      {/* ── STRATEGY PANEL ── */}
      {panelView === 'strategy' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            <Kpi
              label="NAV"
              value={pending && navVal == null ? <Spin /> : fmt$(navVal)}
              sub={pending
                ? (navVal == null ? 'loading…' : `stored EOD · refreshing live view…`)
                : (isPool ? 'equity · infused + P&L' : 'total equity') + divSub}
            />
            <Kpi
              label="Gross Positions"
              value={pending ? <Spin /> : fmt$(grossVal)}
              sub={pending ? 'loading…' : (levX && isPool ? `deployed · ${levX.toFixed(2)}× NAV` : 'deployed book')}
            />
            <Kpi
              label={asOf ? `P&L · ${asOf}` : 'Last close P&L'}
              value={signed$(strat?.dailyPnl)}
              valueClass={pnlClr(strat?.dailyPnl)}
              sub={`last marked close${portToday == null ? '' : ` · ${fmtPct(portToday * 100)}`}${spyToday == null ? '' : ` · SPX ${fmtPct(spyToday * 100)}`}`}
            />
            <Kpi
              label="Cash"
              value={pending ? <Spin /> : fmt$(cashVal)}
              valueClass={cashIsReal ? pnlClr(cashVal) : undefined}
              sub={pending ? 'loading…' : (cashIsReal ? `actual cash · incl. short/hedge proceeds${divCash != null ? ` · settled divs ${fmt$(divCash)}` : ''}` : (cashPct == null ? 'uninvested' : `${cashPct.toFixed(1)}% of AUM`))}
            />
            <Kpi
              label={windowPnl ? `P&L · ${windowPnl.label}` : 'P&L'}
              value={pending && windowPnl?.isFull ? <Spin /> : signed$(windowPnl?.usd)}
              valueClass={pnlClr(windowPnl?.usd)}
              sub={windowPnl?.pct == null ? '' : `${fmtPct(windowPnl.pct)}${windowPnl.isFull && strat ? ` · since ${strat.inceptionDate}` : ''}`}
            />
            <Kpi label="Sharpe" value={sharpe == null ? '—' : sharpe.toFixed(2)} valueClass="text-brand-gold" sub={vol == null ? 'since inception' : `vol ${(vol * 100).toFixed(1)}% · since incep.`} />
          </div>

          {/* compressed TWR hero */}
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
              <div className="flex items-baseline gap-2">
                <span className="inline-block w-3 h-[3px] rounded self-center" style={{ background: 'var(--brand-gold)' }} />
                <span className="text-xs text-gray-500">Portfolio</span>
                <span className={`font-mono text-[20px] tabular-nums ${twrWin.windowReturn == null ? 'text-gray-400' : pnlClr(twrWin.windowReturn)}`}>
                  {twrWin.windowReturn == null ? '—' : fmtPct(twrWin.windowReturn * 100)}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="inline-block w-3 h-[3px] rounded self-center" style={{ background: '#d4696f' }} />
                <span className="text-xs text-gray-500">SPX</span>
                <span className={`font-mono text-[20px] tabular-nums ${spyWin.windowReturn == null ? 'text-gray-400' : pnlClr(spyWin.windowReturn)}`}>
                  {spyWin.windowReturn == null ? '—' : fmtPct(spyWin.windowReturn * 100)}
                </span>
              </div>
              <div className="ml-auto inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-[#0d1117]">
                {RANGES.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    className={`px-2.5 py-1 text-[10.5px] rounded-md transition-colors ${
                      range === r ? 'bg-brand-gold text-[#14171d] font-semibold' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >{RANGE_LABELS[r]}</button>
                ))}
              </div>
            </div>
            {twrLoading && !twr ? (
              <div className="h-[120px] skeleton rounded-xl" />
            ) : (twr?.series?.length ? (
              <MiniTwrChart points={twrWin.points} spyPoints={spyWin.points} />
            ) : (
              <div className="h-[120px] flex items-center justify-center text-xs text-gray-400 italic">
                No TWR history yet — EOD snapshots build the curve nightly.
              </div>
            ))}
            <div className="flex justify-end mt-2 text-[10.5px] text-gray-400">
              Hover to scrub · full curve → Performance tab
            </div>
          </div>

          {/* allocation & holdings — Security table (MV/%/P&L) · Bucket tree · Sector, donut + Book P&L strip */}
          <AllocationBlock tree={allocTree} sectors={allocTree?.sectors} loading={allocLoading} />

          {/* ledger vs tracker vs broker reconciliation (collapsed; broker loads on expand) */}
          <ReconcileTable
            rows={reconRows}
            brokerBySymbol={brokerBySymbol}
            brokerLoading={ibkrLoading}
            onExpand={onReconExpand}
            loading={reconLoading}
          />

          {/* per-security, per-trading-day netted executions (strategy-level, own data) */}
          <ExecutionsBlock
            rows={exec?.rows}
            meta={exec}
            loading={execLoading}
            days={execDays}
            onDaysChange={setExecDays}
          />
        </>
      )}

      {/* ── IBKR (broker) PANEL ── */}
      {panelView === 'ibkr' && (
        <IbkrPanel
          ibkr={ibkr}
          loading={ibkrLoading}
          error={ibkrError}
          onRelogin={handleRelogin}
          reloggingIn={reloggingIn}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default PortfolioV2;
